import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, IconButton, Input, Text } from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import { RichTextField } from '../../../../components/RichTextField'
import { ICourseModuleFields } from '../../../../services/CoursesService/interfaces'

export const baseModule = (): ICourseModuleFields => ({
  _id: crypto.randomUUID(),
  title: '',
  content: ''
})

/** Mesma regra da API: o título é obrigatório; o conteúdo pode ficar vazio. */
export const validateModules = (modules: ICourseModuleFields[]): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  modules.forEach((module, index) => {
    if (module.title.trim() === '') {
      errors[`modules.${index}.title`] = ['O título do módulo é obrigatório.']
    }
  })
  return errors
}

interface IProps {
  modules: ICourseModuleFields[]
  onChange: (next: ICourseModuleFields[]) => void
  disabled?: boolean
  /** Erros por caminho com ponto (`modules.0.title`), como a API devolve. */
  fieldErrors?: Record<string, string[]>
}

/**
 * Módulos da página do curso presencial (ABRA-406): título e conteúdo, na ordem em que o site
 * mostra. Um módulo sem conteúdo aparece no site sem o botão de abrir.
 */
export function CourseModulesEditor({ modules, onChange, disabled, fieldErrors }: IProps) {
  const add = () => onChange([...modules, baseModule()])

  const update = (id: string, field: 'title' | 'content', value: string) =>
    onChange(modules.map((m) => (m._id === id ? { ...m, [field]: value } : m)))

  const remove = (id: string) => onChange(modules.filter((m) => m._id !== id))

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= modules.length) return
    const next = [...modules]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const errorFor = (index: number, field: 'title' | 'content') => fieldErrors?.[`modules.${index}.${field}`]?.[0]

  return (
    <>
      {modules.map((module, index) => (
        <Box key={module._id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} mt={3}>
          <Flex justifyContent="space-between" alignItems="center" mb={3}>
            <Text fontWeight="bold" fontSize="sm">
              Módulo {index + 1}
            </Text>
            <Flex align="center" gap={1}>
              <IconButton
                aria-label="Mover módulo para cima"
                icon={<ChevronUpIcon boxSize={5} />}
                size="xs"
                variant="ghost"
                onClick={() => move(index, -1)}
                isDisabled={disabled || index === 0}
              />
              <IconButton
                aria-label="Mover módulo para baixo"
                icon={<ChevronDownIcon boxSize={5} />}
                size="xs"
                variant="ghost"
                onClick={() => move(index, 1)}
                isDisabled={disabled || index === modules.length - 1}
              />
              <Button
                size="xs"
                colorScheme="red"
                variant="ghost"
                onClick={() => remove(module._id)}
                disabled={disabled}
              >
                Remover
              </Button>
            </Flex>
          </Flex>

          <Flex direction="column" gap={3}>
            <FormField label="Título" error={errorFor(index, 'title')} isRequired>
              <Input
                size="sm"
                value={module.title}
                onChange={(e) => update(module._id, 'title', e.target.value)}
                disabled={disabled}
                maxLength={255}
                placeholder="Ex.: Módulo 1 - Introdução ao Photoshop"
              />
            </FormField>

            <RichTextField
              label="Conteúdo"
              error={errorFor(index, 'content')}
              value={module.content}
              onChange={(content) => update(module._id, 'content', content)}
              placeholder="Tópicos do módulo..."
              isReadOnly={disabled}
            />
          </Flex>
        </Box>
      ))}

      <Button size="sm" variant="outline" colorScheme="gray" onClick={add} disabled={disabled} mt={3}>
        + Adicionar módulo
      </Button>
    </>
  )
}
